import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { fromNullable, Maybe } from 'maybeasy';
import { T } from '../../../../../Translations';
import { learningCollectionsStore } from '../../../../../stores/LearningCollectionsStore';
import {
  LearningCollectionProvider,
  useLearningCollection,
} from '../../CurrentCollectionStep/BuilderV2/LearningCollectionContext';
import ReadyBuilder from '../../CurrentCollectionStep/BuilderV2/ReadyBuilder';
import PreviewSkeleton from './PreviewSkeleton';
import {
  EditLearningCollectionResource,
  ExperienceResourceArray,
  isSystemManaged,
  LearningCollectionResourceWithScheduledPrograms,
  ScheduledProgram,
} from '../../CurrentCollectionStep/BuilderV2/Types';
import { usePreviewLicense } from './PreviewLicenseContext';
import { usePreviewBuilder } from './usePreviewBuilder';

// DVLP-7601: preview-mode analog of Edit/Builder — reuses the same context and
// ReadyBuilder chain but fetches from the preview endpoint and hands the
// selected simulate PLT to it.

interface Props {
  id: string;
}

// Same date-initialization helper as edit mode (see Edit/Builder). Preview
// still needs scheduled_programs entries so the read-only cards can render.
function getAllScheduledDateIds(
  experienceId: number,
  allExperiences: ExperienceResourceArray,
): number[] {
  const experience = allExperiences.find((exp) => exp.payload.id === experienceId);
  if (!experience) return [];
  return experience.payload.program_instance_options.scheduled.map((date) => date.id);
}

function initializeScheduledPrograms(
  resource: EditLearningCollectionResource,
  catalog: Maybe<ExperienceResourceArray>,
): LearningCollectionResourceWithScheduledPrograms {
  const existingScheduledPrograms = fromNullable(
    (resource.payload as LearningCollectionResourceWithScheduledPrograms['payload'])
      .scheduled_programs,
  ).getOrElseValue([]);

  const allExperienceIds = resource.payload.product_collections.flatMap(
    (pc) => pc.payload.experiences,
  );

  const existingEntriesMap = new Map(existingScheduledPrograms.map((sp) => [sp.id, sp]));

  const scheduledPrograms: ScheduledProgram[] = allExperienceIds.map((id) => {
    const existingEntry = fromNullable(existingEntriesMap.get(id));

    // Until the catalog has loaded nothing tells a live run from an ended one,
    // so the stored selection stands as saved (see Edit/Builder).
    return catalog
      .map((allExperiences) => {
        const allDates = getAllScheduledDateIds(id, allExperiences);

        // DVLP-7865: keep the stored selection minus runs that have ended. A
        // selection with nothing left falls through to the default below, so
        // the next save replaces the expired ids with upcoming runs.
        const liveSelection = existingEntry
          .map((entry) => entry.program_ids.filter((programId) => allDates.includes(programId)))
          .getOrElseValue([]);
        if (liveSelection.length > 0) {
          return { id, program_ids: liveSelection };
        }

        return { id, program_ids: allDates };
      })
      .getOrElse(() => existingEntry.getOrElseValue({ id, program_ids: [] }));
  });

  return {
    ...resource,
    payload: { ...resource.payload, scheduled_programs: scheduledPrograms },
  };
}

// DVLP-7601: preview-time filter. `hideUnlicensed` drops experiences that the
// simulated PLT does not entitle; then any section left empty is dropped too
// — the participant view never renders an empty section, so preview must not
// either. When `hideUnlicensed` is false (toggle on) we keep all courses but
// still drop sections that were already empty. Every section, the catalog
// included, carries what the organization's participants see today
// (DVLP-8018), so locking is the only per-license difference. System-managed sections
// (recommendations, group coaching) resolve per participant, so the server
// sends them empty; they are kept so their placeholder explains that.
function filterForPreview(
  resource: EditLearningCollectionResource,
  entitledIds: Set<number>,
  hideUnlicensed: boolean,
): EditLearningCollectionResource {
  return {
    ...resource,
    payload: {
      ...resource.payload,
      product_collections: resource.payload.product_collections
        .map((pc) => ({
          ...pc,
          payload: {
            ...pc.payload,
            experiences: hideUnlicensed
              ? pc.payload.experiences.filter((id) => entitledIds.has(id))
              : pc.payload.experiences,
          },
        }))
        .filter((pc) => isSystemManaged(pc.payload.kind) || pc.payload.experiences.length > 0),
    },
  };
}

// An observer so it re-renders when the catalog store lands (see Edit/Builder).
const PreviewBuilderContent = observer(function PreviewBuilderContent({
  resource,
}: {
  resource: EditLearningCollectionResource;
}) {
  const { resourceState, setResourceState } = useLearningCollection();
  const { showUnlicensed, entitledExperienceIds, selectedPltId } = usePreviewLicense();

  const storeExperiences =
    learningCollectionsStore.state.kind === 'ready'
      ? learningCollectionsStore.state.data.resource.payload.experiences
      : null;

  const catalog = useMemo(() => fromNullable(storeExperiences), [storeExperiences]);

  // Once a PLT is selected we always drop empty sections (participants never
  // see them) and — when the "show unlicensed" toggle is off — also drop the
  // individual cards the simulated PLT doesn't entitle. Before the options
  // fetch lands, pass the full resource through so the page doesn't flash a
  // blank state during initial load.
  const previewReady = selectedPltId.isJust();
  const visibleResource = useMemo(
    () =>
      previewReady ? filterForPreview(resource, entitledExperienceIds, !showUnlicensed) : resource,
    [resource, previewReady, entitledExperienceIds, showUnlicensed],
  );

  const initializedResource = useMemo(
    () => initializeScheduledPrograms(visibleResource, catalog),
    [visibleResource, catalog],
  );

  useEffect(() => {
    setResourceState(initializedResource);
  }, [initializedResource, setResourceState]);

  return resourceState.payload.id.isNothing() ? (
    <PreviewSkeleton />
  ) : (
    <ReadyBuilder stateKind="preview" />
  );
});

function PreviewBuilder({ id }: Props) {
  // Payload fetches once — PLT simulation is applied client-side via
  // entitledExperienceIds in PreviewLicenseContext, so switching the PLT
  // updates cards without re-fetching or remounting the tree.
  const { state } = usePreviewBuilder(id);

  switch (state.kind) {
    case 'loading':
      return <PreviewSkeleton />;
    case 'errored':
      return <T kind="Failed to load preview." />;
    case 'ready':
      return (
        <LearningCollectionProvider>
          <PreviewBuilderContent resource={state.data.resource} />
        </LearningCollectionProvider>
      );
  }
}

export default observer(PreviewBuilder);
